/**
 * Catch-all `[...destinationPath]` params must be a string array.
 * A joined string like `canada/ontario/ottawa` is treated as one segment
 * and the slashes are percent-encoded in canonical and hreflang URLs.
 *
 * @param canonicalPath A joined destination slug, route param array, or missing value.
 * @returns Path segments with empty values removed, never containing `/`.
 *
 * @example destinationPathSegments('canada/ontario/ottawa') // ['canada', 'ontario', 'ottawa']
 */
export function destinationPathSegments(canonicalPath?: Nullable<string | string[]>): string[] {
  if (Array.isArray(canonicalPath)) {
    return canonicalPath.flatMap((segment) => segment.split('/')).filter(Boolean)
  }

  return canonicalPath?.split('/').filter(Boolean) ?? []
}

/**
 * Detects a catch-all param decoded from a percent-encoded slash, e.g. `canada%2Fontario%2Fottawa`.
 * The router splits only on literal slashes, so such URLs arrive as one segment containing `/`.
 *
 * @param destinationPath The `destinationPath` route param.
 * @returns True when any segment still contains `/` and the URL should redirect to its split form.
 *
 * @example hasEncodedDestinationSegments(['canada/ontario/ottawa']) // true
 */
export function hasEncodedDestinationSegments(destinationPath?: Nullable<string | string[]>): boolean {
  return Array.isArray(destinationPath) && destinationPath.some((segment) => segment.includes('/'))
}

/**
 * Joins destination segments into the unencoded path used for API lookups and redirects.
 *
 * @param canonicalPath A joined destination slug, route param array, or missing value.
 * @returns A slash-joined path with empty segments removed.
 *
 * @example destinationPathString(['canada', 'ontario', 'ottawa']) // 'canada/ontario/ottawa'
 */
export function destinationPathString(canonicalPath?: Nullable<string | string[]>): string {
  return destinationPathSegments(canonicalPath).join('/')
}
